import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { BarChart2, X, Link2, Check, Volume2, VolumeX, Pause, Play, ArrowUpRight, Download, FileText } from 'lucide-react';

// ── Social icon SVGs ──────────────────────────────────────────────────────────
const XLogo: React.FC<{ size?: number }> = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.737-8.835L1.254 2.25H8.08l4.253 5.622 5.911-5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);
const LinkedInLogo: React.FC<{ size?: number }> = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);
const FacebookLogo: React.FC<{ size?: number }> = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);

// ── TTS helpers ───────────────────────────────────────────────────────────────
const VOICE_PRIORITY = [
  'Google US English', 'Google UK English Female',
  'Microsoft Aria Online (Natural) - English (United States)',
  'Microsoft Guy Online (Natural) - English (United States)',
  'Samantha (Enhanced)', 'Alex',
];
const pickBestVoice = (voices: SpeechSynthesisVoice[]) => {
  for (const name of VOICE_PRIORITY) {
    const v = voices.find(v => v.name === name);
    if (v) return v;
  }
  return voices.find(v => v.lang.startsWith('en') && !v.localService)
    ?? voices.find(v => v.lang === 'en-US')
    ?? voices.find(v => v.lang.startsWith('en'))
    ?? null;
};
const getVoices = (): Promise<SpeechSynthesisVoice[]> =>
  new Promise(resolve => {
    const v = window.speechSynthesis.getVoices();
    if (v.length > 0) { resolve(v); return; }
    const h = () => { resolve(window.speechSynthesis.getVoices()); window.speechSynthesis.removeEventListener('voiceschanged', h); };
    window.speechSynthesis.addEventListener('voiceschanged', h);
    setTimeout(() => resolve(window.speechSynthesis.getVoices()), 1500);
  });
const stripMarkdown = (md: string) =>
  md.replace(/#{1,6}\s+/g,'').replace(/\*\*(.+?)\*\*/g,'$1').replace(/\*(.+?)\*/g,'$1')
    .replace(/\[(.+?)\]\(.+?\)/g,'$1').replace(/---/g,'. ').replace(/\n{2,}/g,'. ').replace(/\n/g,' ').trim();

// ── Types ─────────────────────────────────────────────────────────────────────
interface Source { title: string; url?: string; }
interface Article {
  id: string;
  date: string;
  category: string;
  categoryColor: string;
  title: string;
  excerpt: string;
  content?: string;
  sources?: Source[];
  image?: string;
  publishedAt: string;
}

interface ChartEntry {
  id: string;
  date: string;
  type: string;
  category: string;
  categoryColor: string;
  title: string;
  subtitle: string;
  annotations: Array<string | { text: string; positive: boolean }>;
  source: string;
  note: string;
  pngFile: string;
  publishedAt: string;
}

type FeedItem =
  | { kind: 'article'; data: Article }
  | { kind: 'chart';   data: ChartEntry };

// ── Helpers ───────────────────────────────────────────────────────────────────
function publishedAt(item: FeedItem): number {
  return new Date(
    item.kind === 'article' ? item.data.publishedAt : item.data.publishedAt
  ).getTime();
}

// ── Article Lightbox ─────────────────────────────────────────────────────────
const ArticleLightbox: React.FC<{ article: Article; onClose: () => void; onNavigate?: (page: string, id?: string) => void }> = ({ article, onClose, onNavigate }) => {
  const [copied, setCopied]         = useState(false);
  const [ttsPlaying, setTtsPlaying] = useState(false);
  const [ttsPaused, setTtsPaused]   = useState(false);
  const utteranceRef                = useRef<SpeechSynthesisUtterance | null>(null);
  const ttsSupported                = 'speechSynthesis' in window;
  const [srcOpen, setSrcOpen]       = useState(false);

  // Close on Escape + cancel TTS on unmount
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') { window.speechSynthesis.cancel(); onClose(); } };
    window.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
      window.speechSynthesis.cancel();
    };
  }, [onClose]);

  const articleUrl = `${window.location.origin}/articles/${article.id}`;
  const shareText  = encodeURIComponent(`${article.title} — via @UTXOMacro`);
  const shareUrl   = encodeURIComponent(articleUrl);
  const handleCopy = async () => { await navigator.clipboard.writeText(articleUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  const openShare  = (url: string) => window.open(url, '_blank', 'noopener,noreferrer');

  const startTTS = async () => {
    if (!ttsSupported) return;
    window.speechSynthesis.cancel();
    setTtsPlaying(true); setTtsPaused(false);
    const voices = await getVoices();
    const voice  = pickBestVoice(voices);
    const text   = `${article.title}. ${stripMarkdown(article.content ?? article.excerpt)}`;
    const utt    = new SpeechSynthesisUtterance(text);
    utt.lang = 'en-US'; utt.rate = 1.10; utt.pitch = 1;
    if (voice) utt.voice = voice;
    utt.onend = utt.onerror = () => { setTtsPlaying(false); setTtsPaused(false); };
    utteranceRef.current = utt;
    window.speechSynthesis.speak(utt);
  };
  const pauseTTS  = () => { window.speechSynthesis.pause();  setTtsPaused(true); };
  const resumeTTS = () => { window.speechSynthesis.resume(); setTtsPaused(false); };
  const stopTTS   = () => { window.speechSynthesis.cancel(); setTtsPlaying(false); setTtsPaused(false); };

  const iconBtn = "w-9 h-9 rounded-lg flex items-center justify-center border border-white/10 text-gray-400 hover:text-white hover:border-white/25 transition-all";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
      onClick={() => { window.speechSynthesis.cancel(); onClose(); }}
    >
      <div
        className="relative bg-[#0F0F0F] border border-gray-700/60 rounded-2xl w-full max-w-2xl shadow-2xl flex flex-col"
        style={{ maxHeight: '90vh' }}
        onClick={e => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div className="flex items-start justify-between px-5 pt-5 pb-4 border-b border-white/5 shrink-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className="text-xs font-bold px-2 py-0.5 rounded uppercase tracking-wide"
              style={{ color: article.categoryColor, background: `${article.categoryColor}18` }}
            >{article.category}</span>
            <span className="text-xs text-gray-500">{article.date}</span>
          </div>
          <button onClick={() => { window.speechSynthesis.cancel(); onClose(); }} className="text-gray-500 hover:text-white transition-colors ml-3 shrink-0">
            <X size={18} />
          </button>
        </div>

        {/* ── Scrollable body ── */}
        <div className="overflow-y-auto flex-1 px-5 py-5">
          {/* Title */}
          <h2 className="text-xl font-bold text-white leading-snug tracking-tight mb-4">{article.title}</h2>

          {/* ── Share + Listen toolbar ── */}
          <div className="flex items-center gap-2 flex-wrap mb-5 pb-5 border-b border-white/5">
            <button onClick={() => openShare(`https://twitter.com/intent/tweet?text=${shareText}&url=${shareUrl}`)} title="Share on X" className={iconBtn}>
              <XLogo size={14} />
            </button>
            <button onClick={() => openShare(`https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`)} title="Share on LinkedIn"
              className="w-9 h-9 rounded-lg flex items-center justify-center border border-white/10 text-gray-400 hover:text-[#0A66C2] hover:border-[#0A66C2]/40 transition-all">
              <LinkedInLogo size={14} />
            </button>
            <button onClick={() => openShare(`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`)} title="Share on Facebook"
              className="w-9 h-9 rounded-lg flex items-center justify-center border border-white/10 text-gray-400 hover:text-[#1877F2] hover:border-[#1877F2]/40 transition-all">
              <FacebookLogo size={14} />
            </button>
            <button onClick={handleCopy} title={copied ? 'Copied!' : 'Copy link'} className={iconBtn}>
              {copied ? <Check size={14} className="text-green-400" /> : <Link2 size={14} />}
            </button>

            <div className="w-px h-5 bg-white/10 mx-1" />

            {ttsSupported && !ttsPlaying && (
              <button onClick={startTTS} className="flex items-center gap-2 px-3 h-9 rounded-lg text-sm font-medium border border-white/10 text-gray-300 hover:text-white hover:border-[#F7931A]/40 hover:bg-[#F7931A]/6 transition-all">
                <Volume2 size={14} /> Listen
              </button>
            )}
            {ttsSupported && ttsPlaying && !ttsPaused && (
              <button onClick={pauseTTS} className="flex items-center gap-2 px-3 h-9 rounded-lg text-sm font-medium border border-[#F7931A]/40 bg-[#F7931A]/8 text-[#F7931A] transition-all">
                <Pause size={14} /> Pause
              </button>
            )}
            {ttsSupported && ttsPlaying && ttsPaused && (
              <button onClick={resumeTTS} className="flex items-center gap-2 px-3 h-9 rounded-lg text-sm font-medium border border-[#F7931A]/40 bg-[#F7931A]/8 text-[#F7931A] transition-all">
                <Play size={14} /> Resume
              </button>
            )}
            {ttsSupported && ttsPlaying && (
              <button onClick={stopTTS} title="Stop" className={iconBtn}><VolumeX size={14} /></button>
            )}
            {ttsPlaying && (
              <span className="flex items-center gap-1.5 text-xs text-[#F7931A] ml-1">
                <Volume2 size={12} className="animate-pulse" />
                {ttsPaused ? 'Paused' : 'Listening…'}
              </span>
            )}

            {/* Full article link */}
            {onNavigate && (
              <button
                onClick={() => { window.speechSynthesis.cancel(); onClose(); onNavigate('articles', article.id); }}
                className="ml-auto flex items-center gap-1.5 text-xs text-gray-500 hover:text-[#F7931A] transition-colors"
              >
                Full article <ArrowUpRight size={12} />
              </button>
            )}
          </div>

          {/* Hero image */}
          {article.image && (
            <div className="mb-5 rounded-xl overflow-hidden border border-white/5" style={{ maxHeight: 280 }}>
              <img src={`/${article.image}`.replace('//','/') } alt={article.title} className="w-full object-cover" style={{ maxHeight: 280 }} />
            </div>
          )}

          {/* Excerpt lede */}
          {article.excerpt && (
            <p className="text-gray-400 leading-relaxed mb-5 border-l-2 border-[#F7931A]/50 pl-3 text-sm">{article.excerpt}</p>
          )}

          {/* Body */}
          {article.content && (
            <div className="prose prose-invert prose-sm max-w-none
              prose-headings:font-bold prose-headings:text-white prose-headings:tracking-tight
              prose-h2:text-sm prose-h2:mt-6 prose-h2:mb-2
              prose-h3:text-sm prose-h3:mt-4 prose-h3:mb-1
              prose-p:text-gray-300 prose-p:leading-7 prose-p:mb-4
              prose-strong:text-white prose-strong:font-bold
              prose-a:text-[#F7931A] prose-a:no-underline hover:prose-a:underline
              prose-hr:border-white/8
              prose-blockquote:border-l-[#F7931A] prose-blockquote:text-gray-400
              prose-code:text-[#F7931A] prose-code:bg-white/5 prose-code:rounded prose-code:px-1">
              <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>{article.content}</ReactMarkdown>
            </div>
          )}

          {/* Sources */}
          {article.sources && article.sources.length > 0 && (
            <div className="mt-8 border-t border-white/5">
              <button
                onClick={() => setSrcOpen(o => !o)}
                className="w-full flex items-center justify-between pt-4 pb-2 group"
              >
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-widest group-hover:text-gray-300 transition-colors">
                  Sources ({article.sources.length})
                </span>
                <span className={`text-gray-600 group-hover:text-gray-300 transition-transform duration-200 inline-block ${srcOpen ? 'rotate-180' : ''}`}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9" /></svg>
                </span>
              </button>
              {srcOpen && (
                <ul className="space-y-1.5 pb-4">
                  {article.sources.map((s, i) => (
                    <li key={i} className="text-xs text-gray-500">
                      {s.url
                        ? <a href={s.url} target="_blank" rel="noopener noreferrer" className="hover:text-[#F7931A] transition-colors">{s.title}</a>
                        : s.title}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Chart helpers ────────────────────────────────────────────────────────────
const handleChartDownload = async (chart: ChartEntry) => {
  try {
    const res  = await fetch(chart.pngFile);
    const blob = await res.blob();
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `utxomacro-${chart.id}.png`; a.click();
    URL.revokeObjectURL(url);
  } catch { window.open(chart.pngFile, '_blank'); }
};

const ChartActionBar: React.FC<{ chart: ChartEntry }> = ({ chart }) => {
  const [copied, setCopied] = useState(false);
  const shareUrl  = encodeURIComponent(`${window.location.origin}/charts`);
  const shareText = encodeURIComponent(`${chart.title} — via @UTXOMacro`);
  const base = 'flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border transition-all border-white/10 text-gray-400 hover:text-white hover:border-white/25';
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <button onClick={() => handleChartDownload(chart)} className={base}>
        <Download size={12} /> Download
      </button>
      <a
        href={`https://twitter.com/intent/tweet?text=${shareText}&url=${shareUrl}`}
        target="_blank" rel="noopener noreferrer" className={base}
      >
        <XLogo size={12} /> Share
      </a>
      <button
        onClick={() => { navigator.clipboard.writeText(decodeURIComponent(shareUrl)); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
        className={base}
      >
        {copied ? <Check size={12} className="text-green-400" /> : <Link2 size={12} />}
        {copied ? 'Copied!' : 'Copy link'}
      </button>
    </div>
  );
};

// ── Chart Lightbox ────────────────────────────────────────────────────────────
const ChartLightbox: React.FC<{ chart: ChartEntry; onClose: () => void }> = ({ chart, onClose }) => {
  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const annotations = chart.annotations ?? [];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative bg-gray-900 border border-gray-700 rounded-2xl max-w-3xl w-full shadow-2xl overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-gray-800">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span
                className="text-xs font-bold px-2 py-0.5 rounded uppercase tracking-wide"
                style={{ color: chart.categoryColor, background: `${chart.categoryColor}18` }}
              >
                {chart.category}
              </span>
              <span className="text-xs text-gray-500 bg-gray-800 px-2 py-0.5 rounded uppercase tracking-wide flex items-center gap-1">
                <BarChart2 size={10} /> Daily Chart
              </span>
            </div>
            <h3 className="text-white font-semibold text-sm leading-snug">{chart.title}</h3>
            {chart.subtitle && <p className="text-gray-400 text-xs mt-0.5">{chart.subtitle}</p>}
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-white transition-colors ml-4 shrink-0 mt-0.5"
          >
            <X size={18} />
          </button>
        </div>

        {/* Chart image */}
        <div className="bg-black px-4 py-4">
          <img
            src={chart.pngFile}
            alt={chart.title}
            className="w-full rounded-lg"
            style={{ maxHeight: 420, objectFit: 'contain' }}
          />
        </div>

        {/* Annotations + meta */}
        <div className="px-5 py-4 border-t border-gray-800 space-y-3">
          {annotations.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {annotations.map((a, i) => {
                const text     = typeof a === 'string' ? a : a.text;
                const positive = typeof a === 'string' ? true : (a as any).positive !== false;
                return (
                  <span
                    key={i}
                    className="text-xs font-semibold px-2 py-0.5 rounded border"
                    style={{
                      color:       positive ? '#F7931A' : '#FF3B3B',
                      background:  positive ? 'rgba(247,147,26,0.08)' : 'rgba(255,59,59,0.10)',
                      borderColor: positive ? 'rgba(247,147,26,0.22)' : 'rgba(255,59,59,0.35)',
                    }}
                  >
                    {text}
                  </span>
                );
              })}
            </div>
          )}
          <ChartActionBar chart={chart} />
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>{chart.source}</span>
            <span>{chart.date}</span>
          </div>
          {chart.note && <p className="text-xs text-gray-500 italic">{chart.note}</p>}
        </div>
      </div>
    </div>
  );
};

// ── Feed Card ─────────────────────────────────────────────────────────────────
const FeedCard: React.FC<{
  item: FeedItem;
  onArticleClick: (id: string) => void;
  onChartClick: (chart: ChartEntry) => void;
}> = ({ item, onArticleClick, onChartClick }) => {
  const color = item.data.categoryColor;
  const isChart = item.kind === 'chart';

  const handleClick = () => {
    if (item.kind === 'article') onArticleClick(item.data.id);
    else onChartClick(item.data);
  };

  // Thumbnail
  const thumb = isChart
    ? (item.data as ChartEntry).pngFile
    : (item.data as Article).image
      ? `/${(item.data as Article).image}`.replace('//', '/')
      : null;

  const title    = item.data.title;
  const date     = item.data.date;
  const excerpt  = isChart
    ? (item.data as ChartEntry).subtitle ?? ''
    : (item.data as Article).excerpt ?? '';
  const ctaLabel = isChart ? 'View Chart →' : 'Read →';

  return (
    <div
      onClick={handleClick}
      className="border border-white/5 rounded-xl overflow-hidden transition-all cursor-pointer group relative flex flex-col"
      style={{
        background: `radial-gradient(ellipse at top left, ${color}09 0%, #111111 60%)`,
        boxShadow: `0 0 0 1px ${color}00`,
      }}
      onMouseEnter={e => (e.currentTarget.style.boxShadow = `0 0 24px 0 ${color}18, 0 0 0 1px ${color}25`)}
      onMouseLeave={e => (e.currentTarget.style.boxShadow = `0 0 0 1px ${color}00`)}
    >
      {/* Thumbnail */}
      {thumb && (
        <div className="w-full h-32 overflow-hidden bg-gray-900">
          <img
            src={thumb}
            alt={title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        </div>
      )}

      {/* Content — flex column, fills remaining card height so CTA always pins to bottom */}
      <div className="p-4 flex flex-col flex-1">
        {/* Badges */}
        <div className="flex items-center gap-1.5 mb-2">
          <span
            className="text-xs font-bold px-1.5 py-0.5 rounded uppercase tracking-wide"
            style={{ color, background: `${color}18` }}
          >
            {item.data.category}
          </span>
          {isChart ? (
            <span className="text-xs text-gray-500 bg-gray-800 px-1.5 py-0.5 rounded uppercase tracking-wide flex items-center gap-1">
              <BarChart2 size={9} /> Chart
            </span>
          ) : (
            <span className="text-xs text-gray-500 bg-gray-800 px-1.5 py-0.5 rounded uppercase tracking-wide flex items-center gap-1">
              <FileText size={9} /> Article
            </span>
          )}
        </div>

        {/* Title — clamped to 2 lines, min-height reserves space for short titles */}
        <h3 className="font-semibold text-white text-sm leading-snug mb-2 group-hover:text-gray-100 transition-colors line-clamp-2" style={{ minHeight: '2.625rem' }}>
          {title}
        </h3>

        {/* Excerpt — fixed 2-line min-height keeps CTA position consistent */}
        <p className="text-gray-500 text-xs leading-relaxed line-clamp-2 mb-3" style={{ minHeight: '2.5rem' }}>
          {excerpt || '\u00A0'}
        </p>

        {/* CTA row — always pinned to bottom */}
        <div className="flex items-center justify-between mt-auto">
          <span className="text-gray-600 text-xs">{date}</span>
          <span className="text-xs font-medium transition-colors" style={{ color }}>
            {ctaLabel}
          </span>
        </div>
      </div>
    </div>
  );
};

// ── StoryGrid ─────────────────────────────────────────────────────────────────
interface StoryGridProps {
  onNavigate?: (page: string, articleId?: string) => void;
}

const StoryGrid: React.FC<StoryGridProps> = ({ onNavigate }) => {
  const [feed, setFeed]             = useState<FeedItem[]>([]);
  const [loading, setLoading]       = useState(true);
  const [lightbox, setLightbox]     = useState<ChartEntry | null>(null);
  const [articleModal, setArticleModal] = useState<Article | null>(null);

  useEffect(() => {
    Promise.all([
      fetch('/data/articles.json').then(r => r.ok ? r.json() : []).catch(() => []),
      fetch('/data/charts.json').then(r => r.ok ? r.json() : []).catch(() => []),
    ]).then(([articles, charts]: [Article[], ChartEntry[]]) => {
      // Sort each pool by recency independently
      const articleItems: FeedItem[] = (articles ?? [])
        .map(a => ({ kind: 'article' as const, data: a }))
        .sort((a, b) => publishedAt(b) - publishedAt(a));
      const chartItems: FeedItem[] = (charts ?? [])
        .map(c => ({ kind: 'chart' as const, data: c }))
        .sort((a, b) => publishedAt(b) - publishedAt(a));

      // Deduplicate charts: only the most recent chart of each type is eligible.
      // This prevents the same chart (e.g. two CPI charts) from appearing side-by-side.
      const seenChartTypes = new Set<string>();
      const deduplicatedChartItems = chartItems.filter(item => {
        const type = (item.data as ChartEntry).type;
        if (seenChartTypes.has(type)) return false;
        seenChartTypes.add(type);
        return true;
      });

      // Interleave: article, chart, article, chart… for a balanced 9-card grid
      const interleaved: FeedItem[] = [];
      let ai = 0, ci = 0;
      while (interleaved.length < 9) {
        // Prefer article on even slots, chart on odd — fall back to whichever has items
        const wantArticle = interleaved.length % 2 === 0;
        if (wantArticle && ai < articleItems.length) {
          interleaved.push(articleItems[ai++]);
        } else if (!wantArticle && ci < deduplicatedChartItems.length) {
          interleaved.push(deduplicatedChartItems[ci++]);
        } else if (ai < articleItems.length) {
          interleaved.push(articleItems[ai++]);
        } else if (ci < deduplicatedChartItems.length) {
          interleaved.push(deduplicatedChartItems[ci++]);
        } else {
          break; // ran out of content
        }
      }
      setFeed(interleaved);
      setLoading(false);
    });
  }, []);

  const handleArticleClick = useCallback((id: string) => {
    const found = feed.find(f => f.kind === 'article' && f.data.id === id);
    if (found && found.kind === 'article') {
      setArticleModal(found.data as Article);
    } else if (onNavigate) {
      onNavigate('articles', id);
    }
  }, [feed, onNavigate]);

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      {/* Section header */}
      <div className="mb-10">
        <h2 className="text-2xl font-bold mb-1 text-gradient">Latest Intelligence</h2>
        <div className="w-12 h-0.5 bg-[#F7931A] rounded" />
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="border border-white/5 rounded-xl h-48 bg-gray-900/40 animate-pulse" />
          ))}
        </div>
      ) : feed.length === 0 ? (
        <p className="text-gray-500 text-sm">No content published yet.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {feed.map((item, i) => (
            <FeedCard
              key={i}
              item={item}
              onArticleClick={handleArticleClick}
              onChartClick={setLightbox}
            />
          ))}
        </div>
      )}

      {lightbox && (
        <ChartLightbox chart={lightbox} onClose={() => setLightbox(null)} />
      )}
      {articleModal && (
        <ArticleLightbox
          article={articleModal}
          onClose={() => setArticleModal(null)}
          onNavigate={onNavigate}
        />
      )}
    </section>
  );
};

export default StoryGrid;
